/* ============================================================
   bm-offer-nudge.js
   Cart drawer offer completion nudge (US store).

   The nudge markup itself is rendered server side by
   snippets/bm-offer-nudge.liquid, so if this script fails the
   component still shows the correct state. This script only adds:
     1. one tap add to cart on the free scent tiles
     1b. in drawer add to cart for the "You might like..." upsells, which the
         theme cannot do on this store (see the block comment there)
     2. the FREE flag on the matching cart line items
     3. the progress bar fill animation after a re-render

   No price text is ever written by this file. The line prices, the FREE
   wording and the offer summary are all rendered by Liquid in
   snippets/cart-drawer.liquid, so they re-render from the server on every
   sections response and can never drift from the cart.

   Everything is defensive. Any failure is swallowed and the
   drawer is left exactly as the server rendered it.
   ============================================================ */
(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  var ROOT_SELECTOR = '[data-bm-offer]';
  var TILE_SELECTOR = '[data-bm-add]';
  var FLAG_CLASS = 'bm-free-flag';
  var syncing = false;
  var syncTimer = null;

  function safe(fn) {
    try {
      return fn();
    } catch (e) {
      return null;
    }
  }

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function openDrawerFallback() {
    var drawer = getCartDrawer();
    if (drawer && typeof drawer.open === 'function') {
      var opened = safe(function () {
        drawer.open();
        return true;
      });
      if (opened) return true;
    }
    return false;
  }

  /* ---------- 1. add to cart ---------- */

  function addFreeScent(tile) {
    var variantId = tile.getAttribute('data-bm-variant');
    if (!variantId) return false;

    var cartDrawer = getCartDrawer();
    var body = { items: [{ id: variantId, quantity: 1 }] };

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      var sections = safe(function () {
        return cartDrawer.getSectionsToRender().map(function (s) {
          return s.id;
        });
      });
      if (sections && sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      safe(function () {
        cartDrawer.setActiveElement(document.activeElement);
      });
    }

    tile.classList.add('is-loading');
    tile.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (!data || data.status) {
          // Shopify returns a status code on error. Fall back to the cart page.
          tile.classList.remove('is-loading');
          tile.removeAttribute('aria-busy');
          return;
        }

        var drawer = getCartDrawer();
        if (drawer && data.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
          if (drawer.classList.contains('is-empty')) {
            drawer.classList.remove('is-empty');
          }
          var inner = drawer.querySelector('.drawer__inner');
          if (inner && inner.classList.contains('is-empty')) {
            inner.classList.remove('is-empty');
          }
          var items = drawer.querySelector('cart-drawer-items');
          if (items && items.classList.contains('is-empty')) {
            items.classList.remove('is-empty');
          }
          scheduleSync(0);
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        tile.classList.remove('is-loading');
        tile.removeAttribute('aria-busy');
      });

    return true;
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var tile = target.closest(TILE_SELECTOR);
      if (!tile) return;
      if (tile.classList.contains('is-loading')) {
        event.preventDefault();
        return;
      }
      // Only intercept when we can actually talk to the drawer.
      // Otherwise let the href (/cart/add?id=...) do the work.
      if (typeof window.fetch !== 'function') return;
      event.preventDefault();
      addFreeScent(tile);
    },
    false
  );

  /* ---------- 1b. "You might like..." upsell add to cart ----------

     ROOT CAUSE. snippets/cart-upsell.liquid wraps each upsell in the theme's
     <product-form> custom element, and assets/product-form.js is the only file
     that defines it. That file is loaded by the theme's own product sections
     (main-product, featured-product, full-product, the collection grids), and
     none of them render on this store: the PDP is the custom scent builder and
     the home page is bm-home. product-form.js is therefore on NO page of
     bombaymusk.co, the element never upgrades, the submit handler that would
     have posted to /cart/add.js with a sections array is never attached, and
     the ADD button falls through to a native form POST to /cart/add. Shopify
     answers that with a 302 to /cart, so the customer is thrown out of the dark
     drawer onto the full cart page.

     Rather than load product-form.js into every page for this one button, the
     add is handled here, on the same sections array + renderContents path the
     free scent tiles above already use, so the one dark drawer refreshes in
     place. That also means the offer summary and the FREE chips re-render from
     Liquid on the same response, which matters because an upsell add can cross
     a tier threshold. Safety rule from the tiles applies unchanged: once the
     HTTP add has returned ok we never surface an error to the customer. */

  var UPSELL_SELECTOR = '#cart-drawer-upsell, #cart-drawer-upsell-wrapper';

  function upsellSetLoading(button, on) {
    if (!button) return;
    if (on) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
    }
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) {
      if (on) spinner.classList.remove('hidden');
      else spinner.classList.add('hidden');
    }
  }

  function addUpsell(form, button) {
    // Single variant upsells carry <input type="hidden" name="id">, multi
    // variant ones carry <select name="id">. Both answer to [name="id"].
    var field = form.querySelector('[name="id"]');
    var variantId = field ? field.value : '';
    if (!variantId) return false;

    var qtyField = form.querySelector('[name="quantity"]');
    var qty = qtyField ? parseInt(qtyField.value, 10) : 1;
    if (!qty || qty < 1) qty = 1;

    var cartDrawer = getCartDrawer();
    var body = { items: [{ id: variantId, quantity: qty }] };

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      var sections = safe(function () {
        return cartDrawer.getSectionsToRender().map(function (s) {
          return s.id;
        });
      });
      if (sections && sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      safe(function () {
        cartDrawer.setActiveElement(document.activeElement);
      });
    }

    upsellSetLoading(button, true);

    var added = false;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        if (!response.ok) return null;
        return response.json().catch(function () {
          added = true;
          return null;
        });
      })
      .then(function (data) {
        if (data && data.status && !data.items) {
          upsellSetLoading(button, false);
          return;
        }
        added = true;

        var drawer = getCartDrawer();
        var rendered = false;

        if (drawer && data && data.sections && typeof drawer.renderContents === 'function') {
          rendered = !!safe(function () {
            drawer.renderContents(data);
            if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
            var inner = drawer.querySelector('.drawer__inner');
            if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
            var items = drawer.querySelector('cart-drawer-items');
            if (items && items.classList.contains('is-empty')) items.classList.remove('is-empty');
            return true;
          });
        }

        // renderContents replaces #CartDrawer wholesale, so this button is gone
        // either way. Only clear the state if it somehow survived.
        upsellSetLoading(button, false);

        if (rendered) {
          scheduleSync(0);
          return;
        }
        if (!openDrawerFallback()) window.location.href = '/cart';
      })
      .catch(function () {
        if (added) {
          if (!openDrawerFallback()) window.location.href = '/cart';
          return;
        }
        upsellSetLoading(button, false);
      });

    return true;
  }

  function handleUpsellSubmit(event) {
    var form = event.target;
    if (!form || typeof form.closest !== 'function') return;
    if (!form.closest(UPSELL_SELECTOR)) return;
    if (typeof window.fetch !== 'function') return;

    var button = form.querySelector('[type="submit"], [name="add"]');
    if (button && button.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    addUpsell(form, button);
  }

  // Capture phase so this wins even if the theme ever starts loading
  // product-form.js again; a defined <product-form> also calls
  // preventDefault, and a double add is the one outcome worth ruling out.
  document.addEventListener('submit', handleUpsellSubmit, true);

  // Belt and braces for the case where the button is outside its form or a
  // browser skips the submit event: intercept the click too.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var button = target.closest('[name="add"]');
      if (!button) return;
      if (!button.closest(UPSELL_SELECTOR)) return;
      if (typeof window.fetch !== 'function') return;
      var form = button.closest('form');
      if (!form) return;
      event.preventDefault();
      if (button.getAttribute('aria-disabled') === 'true') return;
      addUpsell(form, button);
    },
    true
  );

  /* ---------- 2 + 3. post render sync ---------- */

  function clearFlags() {
    // Never touch flags that Liquid rendered. Only clean up our own.
    var existing = document.querySelectorAll('.' + FLAG_CLASS + ':not([data-bm-server])');
    for (var i = 0; i < existing.length; i++) {
      if (existing[i].parentNode) existing[i].parentNode.removeChild(existing[i]);
    }
  }

  function applyFlags(root) {
    clearFlags();
    // snippets/cart-drawer.liquid renders the FREE chips server side, which also
    // gives them the "N of M FREE" wording. Only fall back to JS if it did not.
    if (document.querySelector('.' + FLAG_CLASS + '[data-bm-server]')) return;
    var spec = root.getAttribute('data-bm-free-lines');
    if (!spec) return;

    var pairs = spec.split(',');
    for (var i = 0; i < pairs.length; i++) {
      var parts = pairs[i].split(':');
      if (parts.length !== 2) continue;

      var row = parseInt(parts[0], 10);
      var units = parseInt(parts[1], 10);
      if (!row || !units || units < 1) continue;

      var line = document.getElementById('CartDrawer-Item-' + row);
      if (!line) continue;

      var host = line.querySelector('.cart-item__details');
      if (!host) continue;

      var anchor = host.querySelector('.cart-item__name');
      var flag = document.createElement('span');
      flag.className = FLAG_CLASS;
      flag.textContent = units > 1 ? units + ' FREE' : 'FREE';

      if (anchor && anchor.parentNode === host) {
        if (anchor.nextSibling) {
          host.insertBefore(flag, anchor.nextSibling);
        } else {
          host.appendChild(flag);
        }
      } else {
        host.insertBefore(flag, host.firstChild);
      }
    }
  }

  function animateFill(root) {
    var fill = root.querySelector('.bm-offer__fill');
    if (!fill) return;

    var target = root.getAttribute('data-bm-fill');
    var pct = parseInt(target, 10);
    if (isNaN(pct)) return;
    if (pct < 0) pct = 0;
    if (pct > 100) pct = 100;

    var reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      fill.style.width = pct + '%';
      return;
    }

    fill.style.width = '0%';
    // Guaranteed settle even if the frame callbacks never fire.
    window.setTimeout(function () {
      fill.style.width = pct + '%';
    }, 420);

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          fill.style.width = pct + '%';
        });
      });
    }
  }

  function sync() {
    var root = getRoot();
    if (!root) {
      clearFlags();
      return;
    }
    if (root.getAttribute('data-bm-ready') === '1') return;

    syncing = true;
    root.setAttribute('data-bm-ready', '1');
    safe(function () {
      applyFlags(root);
    });
    safe(function () {
      animateFill(root);
    });
    window.setTimeout(function () {
      syncing = false;
    }, 0);
  }

  function scheduleSync(delay) {
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      safe(sync);
    }, typeof delay === 'number' ? delay : 60);
  }

  function observe(attempt) {
    if (typeof window.MutationObserver !== 'function') return;

    var drawer = getCartDrawer();
    if (!drawer) {
      // The drawer lives in the layout, but retry once in case of late injection.
      if (!attempt) {
        window.setTimeout(function () {
          safe(function () {
            observe(1);
          });
        }, 1200);
      }
      return;
    }

    var observer = new MutationObserver(function () {
      if (syncing) return;
      scheduleSync(60);
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    safe(sync);
    safe(observe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
